import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Home, Plane, RefreshCw, Undo2, History, Power } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { FAUpdateRow } from "@/components/fa/FAUpdateRow";
import { FARunsList } from "@/components/fa/FARunsList";

const TABS = [["new", "New (home)"], ["changed", "Moved"], ["cancelled", "Removed"], ["away", "Away"], ["runs", "Sync history"]];
const fmt = d => { try { return format(parseISO(d), "d MMM yyyy HH:mm"); } catch { return d || "—"; } };

export default function FAUpdates() {
  const [tab, setTab] = useState("new");
  const [rows, setRows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showIgnored, setShowIgnored] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadSummary = useCallback(() => api.get("/fa-updates/summary").then(r => setSummary(r.data)).catch(() => {}), []);
  const load = useCallback(async () => {
    setLoading(true); setSelected(new Set());
    try {
      if (tab === "runs") { const { data } = await api.get("/fa-updates/runs"); setRuns(data); }
      else { const { data } = await api.get(`/fa-updates?status=${tab}`); setRows(data.fixtures); setIsAdmin(data.is_admin); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load FA updates"); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); loadSummary(); }, [load, loadSummary]);

  const act = async (path, body, ok) => {
    try { const { data } = await api.post(path, body); toast.success(typeof ok === "function" ? ok(data) : ok); load(); loadSummary(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Action failed"); }
  };
  const toggleSync = async (v) => {
    try { await api.put("/admin/fa-updates/settings", { fa_sync_enabled: v }); toast.success(v ? "Overnight FA sync turned on" : "Overnight FA sync turned off"); loadSummary(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const visible = rows.filter(r => tab !== "new" || showIgnored || r.review_status !== "ignored");
  const selectable = visible.filter(r => r.review_status === "pending" && !r.staging);
  const toggleAll = () => setSelected(selected.size === selectable.length ? new Set() : new Set(selectable.map(r => r.id)));

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="fa-updates-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">FA Full-Time</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">FA Updates</h1>
          <p className="opacity-60 text-sm mt-1">{isAdmin ? "Everything the overnight FA sync found across all leagues — review, allocate or undo." : "FA fixture changes for your teams, straight from the overnight sync."}</p>
        </div>
        {summary && (
          <div className="card-tactical rounded-md p-3 text-xs flex flex-wrap items-center gap-4" data-testid="fa-updates-summary">
            <span><strong className="text-[#83B68B] text-lg font-display">{summary.new_home_pending}</strong> awaiting allocation</span>
            <span><strong className="text-amber-300 text-lg font-display">{summary.changed}</strong> moved</span>
            <span><strong className="text-red-300 text-lg font-display">{summary.cancelled}</strong> removed</span>
            <span className="opacity-60 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> last sync {summary.last_cron ? fmt(summary.last_cron.at) : "never"}{summary.last_cron?.skipped ? " (skipped — off)" : ""}</span>
            {isAdmin && (
              <label className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase" data-testid="fa-sync-toggle">
                <Power className={`w-3 h-3 ${summary.fa_sync_enabled ? "text-[#83B68B]" : "text-red-400"}`} />
                <Switch checked={!!summary.fa_sync_enabled} onCheckedChange={toggleSync} data-testid="fa-sync-switch" /> Overnight pull {summary.fa_sync_enabled ? "on" : "off"}
              </label>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="fa-updates-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`fa-updates-tab-${k}`} className={`px-4 py-2 rounded text-[10px] tracking-widest uppercase border inline-flex items-center gap-1.5 ${tab === k ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border hover:border-white/40"}`}>
            {k === "away" ? <Plane className="w-3 h-3" /> : k === "runs" ? <History className="w-3 h-3" /> : k === "new" ? <Home className="w-3 h-3" /> : null}{l}
          </button>
        ))}
      </div>

      {tab === "new" && isAdmin && (
        <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="fa-updates-bulk-bar">
          <label className="inline-flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={selectable.length > 0 && selected.size === selectable.length} onChange={toggleAll} data-testid="fa-updates-select-all" className="accent-[#83B68B]" /> Select all pending ({selectable.length})</label>
          <button type="button" disabled={!selected.size} onClick={() => act("/admin/fa-updates/stage", { fixture_ids: [...selected] }, d => `${d.added} sent to allocation${d.skipped.length ? ` · ${d.skipped.length} skipped` : ""}`)} data-testid="fa-updates-stage-selected" className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">Send selected to allocation</button>
          <button type="button" disabled={!selected.size} onClick={() => act("/admin/fa-updates/ignore", { fixture_ids: [...selected] }, d => `${d.ignored} ignored`)} data-testid="fa-updates-ignore-selected" className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase disabled:opacity-40">Ignore selected</button>
          <label className="inline-flex items-center gap-2 ml-auto opacity-70"><input type="checkbox" checked={showIgnored} onChange={e => setShowIgnored(e.target.checked)} data-testid="fa-updates-show-ignored" className="accent-[#83B68B]" /> Show ignored</label>
        </div>
      )}

      {loading ? <div className="opacity-50 font-display tracking-widest">LOADING…</div>
        : tab === "runs" ? <FARunsList runs={runs} isAdmin={isAdmin} onRevert={(run_id, fa_ids) => act("/admin/fa-updates/revert", { run_id, fa_ids }, d => `${d.reverted} entr${d.reverted === 1 ? "y" : "ies"} reverted`)} />
        : visible.length === 0 ? <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="fa-updates-empty">Nothing here{tab === "new" ? " — all new home fixtures have been dealt with" : ""}.</div>
        : (
          <div className="space-y-2" data-testid="fa-updates-list">
            {visible.map(r => (
              <FAUpdateRow key={r.id} r={r} tab={tab} isAdmin={isAdmin}
                selected={selected.has(r.id)} onSelect={() => setSelected(s => { const n = new Set(s); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })}
                onStage={() => act("/admin/fa-updates/stage", { fixture_ids: [r.id] }, d => d.added ? "Sent to allocation — open Admin → FA Import to place it" : d.skipped[0]?.reason || "Skipped")}
                onIgnore={() => act("/admin/fa-updates/ignore", { fixture_ids: [r.id] }, "Ignored")}
                onUnignore={() => act("/admin/fa-updates/unignore", { fixture_ids: [r.id] }, "Back in the queue")}
                onCancelBooking={() => act("/admin/fa-updates/cancel-booking", { fixture_ids: [r.id] }, d => d.cancelled ? "Booking cancelled — pitch slot is free" : d.skipped[0]?.reason || "Nothing to cancel")} />
            ))}
          </div>
        )}
      {isAdmin && tab === "runs" && <div className="text-[11px] opacity-50 inline-flex items-center gap-1"><Undo2 className="w-3 h-3" /> Reverting removes new fixtures from the queue, puts moved ones back to their previous date/time and clears any coach prompts that run raised. Real bookings are never changed.</div>}
    </div>
  );
}
